import { useState, useEffect, useMemo } from 'react';
import {
  query,
  where,
  getDocs,
  getDoc,          // ← 追加
  setDoc,          // ← 追加
  onSnapshot,
  onSnapshot as fsOnSnapshot,
  orderBy,
  updateDoc,
  limit,
  serverTimestamp, // ← 追加
  doc,
} from "firebase/firestore";
import { tCollection, tDoc, tSubCollection } from "../../firebase";
import { TENANT_ID } from "../../tenant";

const DEBUG_FS = false;
const debug = (...args) => { if (DEBUG_FS) console.log(...args); };
const warn = (...args) => { if (DEBUG_FS) console.warn(...args); };
const error = (...args) => { if (DEBUG_FS) console.error(...args); };

debug("useDashboardData.js LOADED v2026-01-20-0248");

// =========================
// helpers (TOP LEVEL)
// =========================

const pickPayMeta = (p) => {
  const md =
    p?.metadata ||
    p?.payment_intent_data?.metadata ||
    p?.paymentIntent?.metadata ||
    p?.payment_intent?.metadata ||
    p?.checkout_session?.metadata ||
    p?.checkoutSession?.metadata ||
    null;
  return md && typeof md === "object" ? md : {};
};

// path 表示（Query/DocRef どちらでも落ちない）
const refPath = (qOrRef) => {
  try {
    return (
      qOrRef?.path ||
      qOrRef?._query?.path ||
      qOrRef?._query?._path?.segments?.join?.("/") ||
      '(unknown)'
    );
  } catch {
    return '(unknown)';
  }
};

const isDenied = (e) =>
  e?.code === "permission-denied" ||
  e?.code === "PERMISSION_DENIED" ||
  String(e?.message || "").toLowerCase().includes("missing or insufficient permissions");

const getDocSafe = async (label, ref) => {
  console.log("[GETDOC:ONCE]", label, ref?.path);
  try {
    return await getDocOnce(label, ref);
  } catch (e) {
    console.error(
      "[DENIED] getDocOnce:",
      label,
      ref?.path,
      "code=",
      e?.code,
      "message=",
      e?.message
    );
    if (isDenied(e)) return null;
    throw e;
  }
};

const getDocsSafe = async (label, q) => {
  console.log("[GETDOCS:ONCE]", label, refPath(q));
  try {
    return await getDocsOnce(label, q);
  } catch (e) {
    console.error(
      "[DENIED] getDocsOnce:",
      label,
      refPath(q),
      "code=",
      e?.code,
      "message=",
      e?.message
    );
    if (isDenied(e)) return { empty: true, docs: [], size: 0 };
    throw e;
  }
};

// ===== DEBUG: raw onSnapshot detector =====
const debugOnSnapshot = (...args) => {
  try {
    if (DEBUG_FS) {
      const refOrQuery = args?.[0];
      const path =
        refOrQuery?.path ||
        refOrQuery?._query?.path?.canonicalString?.() ||
        '(unknown)';

      console.warn('[RAW_onSnapshot_CALLED] path=', path, 'argsLen=', args.length);
    }
  } catch (e) {}

  return (fsOnSnapshot || onSnapshot)(...args);
};
// 日付解析ヘルパー
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.toDate) return dateStr.toDate();
  if (dateStr instanceof Date) return dateStr;
  try {
    let s = String(dateStr);
    s = s.replace(/年|月/g, '/').replace(/日/g, '').replace(/UTC\+9/, '').trim();
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

const logSub = (label, refOrQuery) => {
  if (!DEBUG_FS) return;
  // eslint-disable-next-line no-console
  console.log(`[SUBSCRIBE] ${label}:`, refPath(refOrQuery), refOrQuery);
};

const logRead = (label, refOrQuery) => {
  if (!DEBUG_FS) return;
  // eslint-disable-next-line no-console
  console.log(`[READ] ${label}:`, refPath(refOrQuery), refOrQuery);
};

const logDenied = (label, refOrQuery, err) => {
  // eslint-disable-next-line no-console
  console.error(`[DENIED] ${label}:`, refPath(refOrQuery), err);
};

const subscribeSafe = (label, refOrQuery, onNext) => {
  logSub(label, refOrQuery);

  return debugOnSnapshot(
    refOrQuery,
    (snap) => {
      try {
        onNext?.(snap);
      } catch (e) {
        console.error(`[SNAPSHOT_HANDLER_ERROR] ${label}:`, refPath(refOrQuery), e);
      }
    },
    (err) => {
      // ★ここが重要：どの購読がDENIEDかを「必ず」特定する
      error(
        `[DENIED] onSnapshot:${label}:`,
        refPath(refOrQuery),
        'code=',
        err?.code,
        'message=',
        err?.message,
        err
      );
    }
  );
};

const getDocOnce = (label, ref) =>
  new Promise((resolve, reject) => {
    // error handler を必ず渡す
    const unsub = debugOnSnapshot(
      ref,
      (snap) => {
        try {
          unsub?.();
        } catch (e) {}
        resolve(snap);
      },
      (err) => {
        try {
          unsub?.();
        } catch (e) {}
        reject(err);
      }
    );
  });

const getDocsOnce = (label, q) =>
  new Promise((resolve, reject) => {
    const unsub = debugOnSnapshot(
      q,
      (snap) => {
        try {
          unsub?.();
        } catch (e) {}
        resolve(snap);
      },
      (err) => {
        try {
          unsub?.();
        } catch (e) {}
        reject(err);
      }
    );
  });

// entitlements remaining計算（DashboardMessageBlock と合わせる）
const getRemaining = (e) => {
  const total = parseInt(e?.quantity_total ?? 0, 10);
  const used = parseInt(e?.quantity_used ?? 0, 10);
  const pending = parseInt(e?.pending_quantity ?? 0, 10);
  return Math.max(0, total - used - pending);
};

// ✅ 追加：Date化（Timestamp / Date / string / number を安全に扱う）
const toDateSafe = (v) => {


// {seconds, nanoseconds} 形式も救済
if (typeof v === 'object' && v?.seconds != null) {
  const ms = Number(v.seconds) * 1000 + Math.floor(Number(v.nanoseconds || 0) / 1e6);
  const d = new Date(ms);
  return !Number.isNaN(d.getTime()) ? d : null;
}



  if (!v) return null;

  if (typeof v?.toDate === 'function') {
    try {
      const d = v.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch (e) {
      return null;
    }
  }

  if (v instanceof Date) return !Number.isNaN(v.getTime()) ? v : null;

  if (typeof v === 'number') {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  if (typeof v === 'string') {
    const d = new Date(v);
    return !Number.isNaN(d.getTime()) ? d : null;
  }

  return null;
};

// ✅ 追加：entitlements から valid_minutes / last_used_at / title / type を揺れ吸収して取る
const getValidMinutesFromEnt = (e) => {
  const v =
    e?.valid_minutes ??
    e?.metadata?.valid_minutes ??
    e?.product_metadata?.valid_minutes ??
    0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const getLastUsedAtFromEnt = (e) => {
  return (
    e?.last_used_at ||
    e?.lastUsedAt ||
    e?.metadata?.last_used_at ||
    e?.metadata?.lastUsedAt ||
    null
  );
};

const getTitleFromEnt = (e) => {
  return (
    e?.title ||
    e?.name ||
    e?.product_name ||
    e?.productName ||
    e?.metadata?.title ||
    e?.metadata?.name ||
    '利用中'
  );
};

const getTypeFromEnt = (e) => {
  const t =
    e?.product_type ||
    e?.type ||
    e?.metadata?.product_type ||
    e?.metadata?.type ||
    'ticket';
  const s = String(t).toLowerCase();
  if (s === 'goods') return 'goods';
  if (s === 'plan') return 'plan';
  return 'ticket';
};

export const useDashboardData = (user) => {
  const tenantId = TENANT_ID;
  const [memberData, setMemberData] = useState(null);
  const [rawPaymentHistory, setRawPaymentHistory] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [productMap, setProductMap] = useState({});

const [inventory, setInventory] = useState({ tickets: [], goods: [] });
const [activeItems, setActiveItems] = useState([]);
const [planPulseItem, setPlanPulseItem] = useState(null);
const [mergedActiveItems, setMergedActiveItems] = useState([]);

  // ✅ 予約用（ActiveTimeBlock 用）
  const [activeReservationItem, setActiveReservationItem] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const [systemMessage, setSystemMessage] = useState(null);
  const [reservationsRaw, setReservationsRaw] = useState([]);
  const [reservations, setReservations] = useState([]); // ← confirmedだけ入れる
  const [pendingReservationCount, setPendingReservationCount] = useState(0);  
 
  const [pendingGifts, setPendingGifts] = useState([]);

  // ✅ 追加：customers の docId（Auth uid と一致しないケースがあるため）
  const [customerUid, setCustomerUid] = useState(null);

  // ✅ 追加：除外判定に使う entitlements
  const [entitlements, setEntitlements] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [dismissedActiveKey, setDismissedActiveKey] = useState(null);
  // ✅ 追加：priceId -> { unit_amount, currency, productId, nickname, metadata }
  const [priceMap, setPriceMap] = useState({});
  // 🔥 loadingはAuthベースにする
  const loading = !user;
  // --- 0. customerUid 解決（Auth uid → customers docId） ---
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30 * 1000); // 30秒で十分
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        if (!user?.uid) {
          if (alive) setCustomerUid(null);
          return;
        }

        const authUid = String(user.uid);
        console.log("[useDashboardData] authUid:", authUid, "email:", user.email || "");

        // 0-A) customers/{authUid} が存在するならそれを使う（server 直読みにして DENIED を確実に捕まえる）
        let refA = null;        
        try {
          refA = tDoc("customers", authUid, tenantId);
          console.log("[GETDOC:SERVER] resolveCustomerUid: direct customers/{authUid}", refA.path);

          const snap = await getDocSafe("resolveCustomerUid: direct customers/{authUid}", refA);

          // ★snap が null（DENIED）なら次へ進む
          if (!snap) {
            console.warn("[GETDOC:SERVER] direct customers/{authUid} denied => continue");
          } else {
            console.log("[GETDOC:SERVER] direct customers/{authUid} exists:", snap.exists());
            if (snap.exists()) {
              if (alive) setCustomerUid(authUid);
              return;
            }
          }
        } catch (e) {
          console.warn("[GETDOC:SERVER] direct customers/{authUid} FAILED (continue):", {
            path: refA?.path,
            code: e?.code,
            msg: e?.message,
          });
        }

        // ... 0-B, 0-C 続き ...

        // 0-B) フィールド探索
        const tryFields = ["auth_uid", "authUid", "firebase_uid", "firebaseUid", "userId", "uid"];
        for (const f of tryFields) {
          let q = null;
          try {
            q = query(tCollection("customers", tenantId), where(f, "==", authUid), limit(1));
            logRead(`resolveCustomerUid: where ${f}==authUid`, q);

            const s = await getDocsSafe(`resolveCustomerUid: where ${f}==authUid`, q);
            if (!s.empty) {
              const id = s.docs[0].id;
              console.log("[useDashboardData] customerUid resolved by field match:", f, "=>", id);
              if (alive) setCustomerUid(id);
              return;
            }
          } catch (e) {
            logDenied(`resolveCustomerUid: where ${f}==authUid`, q, e);
            console.warn("[useDashboardData] customerUid resolve by field failed:", f, e);
          }
        }

        // 0-C) email
        if (user?.email) {
          try {
            const q = query(
              tCollection("customers", tenantId),
              where("email", "==", String(user.email)),
              limit(1)
            );
            logRead("resolveCustomerUid: where email==user.email", q);

            const s = await getDocsSafe("resolveCustomerUid: where email==user.email", q);
            if (!s.empty) {
              const id = s.docs[0].id;
              console.log("[useDashboardData] customerUid resolved by email:", id);
              if (alive) setCustomerUid(id);
              return;
            }
          } catch (e) {
            console.warn("[useDashboardData] customerUid resolve by email failed:", e);
          }
        }

        // ---------------------------------------------
        // 🔥 顧客自動生成（初回ログイン時）
        // ---------------------------------------------
        try {
          const autoRef = tDoc("customers", authUid, tenantId);
          const autoSnap = await getDocSafe("autoCreate customers/{authUid}", autoRef);

          if (!autoSnap || !autoSnap.exists()) {
            console.log("[AUTO CREATE] customers/" + authUid);

            await setDoc(autoRef, {
              auth_uid: authUid,
              email: user?.email || null,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp(),
              source: "auto_create_on_login",
            });
          }

          if (alive) setCustomerUid(authUid);
          return;

        } catch (e) {
          console.warn("[AUTO CREATE FAILED]", e?.message || e);
        }

        console.warn("[useDashboardData] customerUid NOT resolved.");
        if (alive) setCustomerUid(null);
      } catch (e) {
        console.error("[useDashboardData] customerUid resolve fatal:", e);
        if (alive) setCustomerUid(null);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [user?.uid, user?.email, tenantId]);

  // --- 1. 商品マスタ取得 ---
  // --- 1. 商品マスタ取得（products + prices） ---
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(tCollection('products', tenantId));
        const snapshot = await getDocs(q);

        const pMap = {};
        const prMap = {}; // priceMap

        // products を回して prices も読む
        for (const d of snapshot.docs) {
          const data = d.data() || {};
          const type = data.metadata?.product_type || data.metadata?.type;

          pMap[d.id] = { type, name: data.name, metadata: data.metadata || {} };

          // ✅ prices サブコレを読む
          try {

            prMap[data.active_price_id] = {
              unit_amount: data.unit_amount || 0,
              currency: data.currency || "jpy",
              productId: d.id,
              nickname: data.nickname || null,
              metadata: data.metadata || {}
            };

          } catch (e) {
            // prices が無い product があっても落とさない
            console.warn('[fetchProducts] prices load failed', { productId: d.id, err: e?.message || e });
          }
        }

        setProductMap(pMap);
        setPriceMap(prMap);
      } catch (error) {
        console.error(error);
      }
    };

    fetchProducts();
  }, [tenantId]);

  // --- 2. データ監視 ---
  useEffect(() => {

    // ① Authが無いときだけクリア
    if (!user?.uid) {
      setMemberData(null);
      setRawPaymentHistory([]);
      setActiveSubscriptions([]);
      setEntitlements([]);
      return;
    }

    // ② customerUid 未解決なら何もしない（UIは止めない）
    if (!customerUid) {
      return;
    }

    console.log('[useDashboardData] using customerUid:', customerUid);

    const userDocRef = tDoc('customers', String(customerUid), tenantId);
    logSub('customers doc', userDocRef);

    const unsubscribeUser = subscribeSafe(
      "customers doc",
      userDocRef,
      (snap) => {
        const data = snap.exists()
          ? snap.data()
          : { name: "", metadata: { tickets: 0 } };

        setMemberData({
          ...data,
          customerUid: String(customerUid),
          customer_uid: String(customerUid),
        });
      }
    );

    return () => {
      try { unsubscribeUser && unsubscribeUser(); } catch {}
    };

  }, [user?.uid, customerUid, tenantId]);


    // --- 2.1.0 payments 購読（tenants/{tenant}/customers/{customerUid}/payments） ---

  useEffect(() => {
    if (!customerUid) {
      setRawPaymentHistory([]);
      return;
    }

    const ref = tSubCollection('customers', String(customerUid), 'payments', tenantId);
    const q = query(ref, orderBy('created_at', 'desc'), limit(100));

    const unsub = subscribeSafe('customers payments', q, (snap) => {
      
    const items = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => {
        const md = pickPayMeta(p);

        // subscriptionは除外
        if (p.mode === "subscription") return false;

        if (md?.product_type === "plan") return false;

        if (md?.checkout_mode === "subscription") return false;

        return true;
      });

    setRawPaymentHistory(items);
      debug('[payments] snap', { size: snap.size, fromCache: snap.metadata?.fromCache });
    });

    return () => { try { unsub?.(); } catch {} };
  }, [customerUid, tenantId]);
  

  // --- 2.1 entitlements 購読（tenants/{tenant}/customers/{customerUid}/entitlements） ---
  useEffect(() => {
    if (!customerUid) {
      setEntitlements([]);
      return;
    }

    const ref = tSubCollection('customers', String(customerUid), 'entitlements', tenantId);
    console.log('[useDashboardData] entitlements subscribing =>', ref.path);
    const q = query(ref);

    const unsub = subscribeSafe('customers entitlements', q, (snap) => {
      debug('[subscriptions] snap', { size: snap.size, fromCache: snap.metadata?.fromCache, path: refPath(q) });
      const items = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setEntitlements(items);
    });

    return () => {
      try { unsub?.(); } catch (e) {}
    };
  }, [customerUid, tenantId]);

  // --- 2.1.1 subscriptions 購読（tenants/{tenant}/customers/{customerUid}/subscriptions） ---
  useEffect(() => {
    if (!customerUid) {
      setSubscriptions([]);
      setActiveSubscriptions([]);
      return;
    }

    const ref = tSubCollection('customers', String(customerUid), 'subscriptions', tenantId);
    console.log('[useDashboardData] subscriptions subscribing =>', ref.path);

    const q = query(ref);

    const unsub = subscribeSafe('customers subscriptions', q, (snap) => {
      const items = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));

      setSubscriptions(items);

      // “active な月額プラン”だけ欲しいならここで抽出
      const active = items.filter((s) => String(s?.status || '') === 'active');
      setActiveSubscriptions(active);
    });

    return () => {
      try { unsub?.(); } catch (e) {}
    };
  }, [customerUid, tenantId]);

  // --- 2.2 pendingGifts 購読（gift_tokens：グローバル / sender_uid は auth uid） ---
  useEffect(() => {

    console.log("[pendingGifts] customerUid =", customerUid);

    if (!customerUid || !user?.uid || !tenantId) {
      setPendingGifts([]);
      return;
    }

    const ref = tCollection("gift_tokens", tenantId);

    // rules は送り主本人の分だけ一覧を許す。sender_uid(=auth uid)で引く
    const q = query(
      ref,
      where("sender_uid", "==", String(user?.uid || "")),
      where("status", "==", "issued")
    );

    console.log("[pendingGifts query]", {
      tenantId,
      sender_uid: user?.uid,
    });

    const unsub = onSnapshot(q, (snap) => {

      console.log("[pendingGifts snapshot size]", snap.size);

      const items = snap.docs.map((d) => {
        const g = d.data() || {};

        return {
          token: d.id,
          title: g.product_name || "ギフト",
          type: g.product_type || "ticket",
          totalQty: Number(g.quantity || 1),
          url: `${window.location.origin}/gift/receive/${d.id}`,
        };
      });

      console.log("[pendingGifts items]", items);

      setPendingGifts(items);

    }, (err) => {

      console.error("[pendingGifts ERROR]", err);

    });

    return () => unsub();

  }, [customerUid, user?.uid, tenantId]);

  // --- 2.2.5 reservations 購読（tenants/{tenantId}/reservations）---
  // customerUid に依存させない（user.uid で取れる）
  useEffect(() => {
    if (!user?.uid) {
      setReservationsRaw([]);
      setReservations([]);
      setPendingReservationCount(0);
      return;
    }

    // ==========================
    // ① confirmed 購読（表示用）
    // ==========================
    const confirmedQuery = query(
      tCollection("reservations", tenantId),
      where("guestUids", "array-contains", String(user.uid)),
      where("status", "==", "confirmed"),
      orderBy("startAt", "desc")
    );

    const unsubConfirmed = subscribeSafe(
      "reservations_confirmed",
      confirmedQuery,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReservationsRaw(items);     // activeReservationItem 用
        setReservations(items);        // 表示用
      }
    );

    // ==========================
    // ② pending 購読（カウント専用）
    // ==========================
    const pendingQuery = query(
      tCollection("reservations", tenantId),
      where("guestUids", "array-contains", String(user.uid)),
      where("status", "==", "pending_payment")
    );

    const unsubPending = subscribeSafe(
      "reservations_pending",
      pendingQuery,
      (snap) => {
        setPendingReservationCount(snap.size);
      }
    );

    return () => {
      try { unsubConfirmed?.(); } catch {}
      try { unsubPending?.(); } catch {}
    };

  }, [user?.uid, tenantId]);   
  
  
  // --- 2.3 activeItem 生成（entitlements 正本ベース） ---
  useEffect(() => {
    try {
      const now = new Date();
      const src = Array.isArray(entitlements) ? entitlements : [];

      const activeCandidates = src
        .map((e) => {
          const validMin = getValidMinutesFromEnt(e);
          if (!validMin || validMin <= 0) return null;

          const lastUsedAtRaw = getLastUsedAtFromEnt(e);
          if (!lastUsedAtRaw) return null;

          const lastUsedAt = toDateSafe(lastUsedAtRaw);
          if (!lastUsedAt) return null;

          const expireTime = new Date(lastUsedAt.getTime() + validMin * 60000);
          const diffMs = expireTime.getTime() - now.getTime();

          if (diffMs <= 0) return null;

          const remainingMin = Math.max(1, Math.ceil(diffMs / 60000));

          const docId = e?.docId || e?.id || "";
          const activeKey = `${docId}__${lastUsedAt.getTime()}`;

          const type = getTypeFromEnt(e);
          const title = getTitleFromEnt(e);

          const total = Number(e.quantity_total ?? 1);

          const quantity =
            type === "goods"
              ? Math.max(total, 0)
              : 1;

          return {
            activeKey,
            title,
            type,
            quantity,
            remainingMin,
            expireTime,
            expireTimeStr: `${expireTime.getHours()}:${String(
              expireTime.getMinutes()
            ).padStart(2, "0")}`,
          };
        })
        .filter(Boolean);
      activeCandidates.sort(
        (a, b) => a.expireTime.getTime() - b.expireTime.getTime()
      );

      const nextActives = activeCandidates.filter(
        (c) => c.activeKey !== dismissedActiveKey
      );

      setActiveItems(nextActives);
    } catch (e) {
      console.error("[useDashboardData] build activeItem error:", e);
    }
  }, [entitlements, dismissedActiveKey, nowTick]);
  
  // --- 3. 自動確定ロジック ---
  useEffect(() => {
    const checkAndConfirmReservations = async () => {
    const pendingRes = reservationsRaw.filter((r) => r.status === 'pending_payment');      if (pendingRes.length === 0 || rawPaymentHistory.length === 0) return;

      pendingRes.forEach(async (res) => {
        const match = rawPaymentHistory.find((p) => p.metadata?.reservationId === res.id);
        if (match) {
          console.log(`支払い確認: 予約 ${res.id} を確定します`);
          try {
            console.log('[AUTO-CONFIRM] updating:', `tenants/${tenantId}/reservations/${res.id}`);
            await updateDoc(tDoc('reservations', res.id, tenantId), { status: 'confirmed' });
          } catch (e) {
            console.error('ステータス更新失敗', e);
          }
        }
      });
    };
    checkAndConfirmReservations();
  }, [reservationsRaw, rawPaymentHistory, tenantId]);

  // --- 4. システムメッセージ（tenant config に統一） ---
  useEffect(() => {
    const cfgRef = tDoc("config", "dashboard_config", tenantId);

    const unsub = subscribeSafe("config/dashboard_config", cfgRef, (snap) => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setSystemMessage(data);
        console.log("[useDashboardData] systemMessage (tenant):", data);
      } else {
        setSystemMessage(null);
        console.log("[useDashboardData] systemMessage (tenant): not found");
      }
    });

    return () => unsub();
  }, [tenantId]);

  // ✅ reservations から「いま利用中（残り時間表示対象）」を作る

  // --- 予約 ActiveItem 生成（pre/post_minutes 対応版） ---
useEffect(() => {
  const now = Date.now();

  const list = Array.isArray(reservationsRaw) ? reservationsRaw : [];

  const active = list
    .map((r) => {
      const start = toDateSafe(r?.startAt);
      const end = toDateSafe(r?.endAt);

      if (!start || !end) return null;

      const startMs = start.getTime();
      const endMs = end.getTime();

      // 🔥 ここが今回の本質
      const preMin = Number(r?.pre_minutes ?? 0);
      const postMin = Number(r?.post_minutes ?? 0);

      const effectiveStart = startMs - preMin * 60000;
      const effectiveEnd = endMs + postMin * 60000;

      if (!(effectiveStart <= now && now < effectiveEnd)) return null;

      const remainingMin = Math.ceil((effectiveEnd - now) / 60000);

      if (remainingMin <= -1) return null;

      return {
        activeKey: `reservation_${String(r.id)}`,
        type: "reservation",
        title: String(r.title || r.serviceName || "予約利用中"),
        expireTime: new Date(effectiveEnd),
        expireTimeStr: `${String(new Date(effectiveEnd).getHours()).padStart(2, "0")}:${String(new Date(effectiveEnd).getMinutes()).padStart(2, "0")}`,
        remainingMin,
        reservationId: String(r.id),
        startAt: start,
        endAt: end,
        preMinutes: preMin,
        postMinutes: postMin,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.expireTime.getTime() - b.expireTime.getTime());

  setActiveReservationItem(active[0] || null);

}, [reservationsRaw, nowTick]);


  // --- 5. 在庫集計（予約決済は除外する修正を追加） ---
  // --- inventory 集計（entitlements 正本）---
  useEffect(() => {
    const tickets = [];
    const goods = [];

    const upsert = (list, title, qty, type, detail, id) => {
      const existing = list.find((x) => x.title === title);
      if (existing) {
        existing.qty += qty;
        existing.status = `${existing.qty}${type === "ticket" ? "枚" : "個"} 所持`;
        return;
      }
      list.push({
        id,
        title,
        qty,
        status: `${qty}${type === "ticket" ? "枚" : "個"} 所持`,
        type,
        detail,
      });
    };

    const src = Array.isArray(entitlements) ? entitlements : [];
    for (const e of src) {
      const typeRaw = String(e?.product_type || "ticket").toLowerCase();
      const type =
        typeRaw === "goods" ? "goods" :
        typeRaw === "plan" ? "plan" :
        "ticket";

      // plan は inventory から外す（会員表示側に任せる）
      if (type === "plan") continue;

      const remaining = Math.max(
        0,
        Number(e?.quantity_total || 0) - Number(e?.quantity_used || 0) - Number(e?.pending_quantity || 0)
      );
      if (remaining <= 0) continue;

      const title = String(e?.product_name || e?.title || e?.name || "購入商品");
      const detail =
        type === "ticket"
          ? `有効時間: ${Number(e?.valid_minutes || 0)}分`
          : "アイテム";

      if (type === "goods") {
        upsert(goods, title, remaining, "goods", detail, e?.docId || e?.id || title);
      } else {
        upsert(tickets, title, remaining, "ticket", detail, e?.docId || e?.id || title);
      }
    }

    setInventory({ tickets, goods });
  }, [entitlements]);

  // 🔥 plan_active を直接取得（状態機械の真実）
  const planActiveEnt = useMemo(() => {
    return (entitlements || []).find(e => e.docId === "plan_active") || null;
  }, [entitlements]);

const planCard = useMemo(() => {
  const plan = planActiveEnt;

  // 🔥 最初に必ずガード
  if (!plan) return null;

  const statusRaw = String(plan.status || "").toLowerCase();
  if (statusRaw === "expired") return null;

  // 🔥 ここから下で plan を使う
  const activeSub =
    subscriptions.find(s =>
      s.subscription_id === plan.subscription_id
    ) || null;

  const nextRenewalText = (() => {
    const d = toDateSafe(
      activeSub?.current_period_end ||
      activeSub?.current_period_end_s
    );
    return d ? d.toLocaleDateString("ja-JP") : "";
  })();

  const currentPriceId = plan.stripe_price_id;
  const currentAmount =
    currentPriceId && priceMap?.[currentPriceId]
      ? Number(priceMap[currentPriceId].unit_amount || 0)
      : null;

      // 🔥 pending change 表示用データ生成
      let pendingDisplay = null;

      if (plan.pending_change?.target_price_id) {
        const targetPriceId = plan.pending_change.target_price_id;
        const priceInfo = priceMap?.[targetPriceId];

        pendingDisplay = {
          name:
            productMap?.[priceInfo?.productId]?.name ||
            "変更予定プラン",

          amount: Number(priceInfo?.unit_amount || 0),

          effectiveDate: plan.pending_change.effective_at_ms
            ? new Date(plan.pending_change.effective_at_ms).toLocaleDateString("ja-JP")
            : null,
        };
      }

  return {
    title: plan.product_name || "月額プラン",
    currentAmount,
    nextRenewalText,
    hasPendingChange: !!plan.pending_change,
    subscriptionId: activeSub?.subscription_id || "",
    status: { tone: "ok", text: "有効" },

    // 🔥 追加
    cancelAtPeriodEnd: activeSub?.cancel_at_period_end === true,
    cancelAt: activeSub?.cancel_at || null,
    pendingDisplay, // ←追加
  };
}, [planActiveEnt, subscriptions, priceMap]);

useEffect(() => {
  if (!customerUid || !tenantId || !planCard?.title) {
    setPlanPulseItem(null);
    return;
  }

  //const eventRef = tDoc("customers", String(customerUid), "runtime", "last_access_event", tenantId);
  const runtimeRef = tSubCollection("customers", String(customerUid), "runtime", tenantId);
  const eventRef = doc(runtimeRef, "last_access_event");



  let hideTimer = null;
  let lastShownAt = 0;
  let isInitialSnapshot = true;

  const unsub = subscribeSafe("customers runtime last_access_event", eventRef, (snap) => {
    if (!snap.exists()) return;
    if (isInitialSnapshot) {
      isInitialSnapshot = false;
      return;
    }
    const data = snap.data() || {};
    const grantedType = String(data.granted_type || "");
    const sessionType = String(data.session_type || "");
    const at = toDateSafe(data.at) || toDateSafe(data.updated_at);

    if (!at) return;

    const atMs = at.getTime();
    if (atMs === lastShownAt) return;

    const shouldShow =
      grantedType === "plan" ||
      (grantedType === "reentry" && sessionType === "plan");

    if (!shouldShow) return;

    lastShownAt = atMs;

    const expireTime = new Date(Date.now() + 3000);

    setPlanPulseItem({
      activeKey: `plan_pulse_${atMs}`,
      title: planCard.title || "月額プラン",
      type: "plan",
      quantity: 1,
      remainingMin: 1,
      expireTime,
      expireTimeStr: `${expireTime.getHours()}:${String(
        expireTime.getMinutes()
      ).padStart(2, "0")}`,
    });

    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      setPlanPulseItem(null);
    }, 3000);
  });

  return () => {
    if (hideTimer) clearTimeout(hideTimer);
    try { unsub?.(); } catch (e) {}
  };
}, [customerUid, tenantId, planCard?.title]);

useEffect(() => {
  if (planPulseItem) {
    setMergedActiveItems([...activeItems, planPulseItem]);
  } else {
    setMergedActiveItems(activeItems);
  }
}, [activeItems, planPulseItem]);

  // --- 6. メッセージフィルタ（除外判定を entitlements 基準へ修正 + ログ） ---
  const status = { tone: "warn", text: "状態不明" };
  const validSystemMessage = useMemo(() => {
    if (!systemMessage?.active || !systemMessage?.message) return null;

    if (systemMessage.target_attribute && systemMessage.target_attribute !== 'all') {
      const userAttribute = memberData?.attribute || memberData?.metadata?.attribute;
      if (userAttribute !== systemMessage.target_attribute) {
        console.log('[validSystemMessage] target_attribute mismatch', {
          need: systemMessage.target_attribute,
          userAttribute,
        });
        return null;
      }
    }

    if (systemMessage.exclude_door_id) {
      const excludeId = String(systemMessage.exclude_door_id);

      const excluded = (Array.isArray(entitlements) ? entitlements : []).some((e) => {
        const doorIds = Array.isArray(e?.door_ids) ? e.door_ids.map(String) : [];
        if (!doorIds.includes(excludeId)) return false;

        const remaining = getRemaining(e);
        if (remaining > 0) return true;
        if (e?.is_active === true) return true;

        return false;
      });

      console.log('[validSystemMessage] exclude check', {
        excludeId,
        excluded,
        entitlementsCount: (entitlements || []).length,
      });

      if (excluded) return null;
    }

    if (systemMessage.require_purchase_category) {
      const hasPurchased = rawPaymentHistory.some((pay) => {
        if (pay.metadata?.product_type === systemMessage.require_purchase_category) return true;
        return pay.items?.some((item) => {
          const pId = item.product || item.price?.product;
          const pType = productMap[pId]?.type;
          return pType === systemMessage.require_purchase_category;
        });
      });
      if (!hasPurchased) {
        console.log('[validSystemMessage] require_purchase_category mismatch', {
          need: systemMessage.require_purchase_category,
        });
        return null;
      }
    }

    return systemMessage;
  }, [systemMessage, memberData, rawPaymentHistory, productMap, entitlements]);

  // ActiveItem を手動で消す（ExpiredModal を閉じたら UI を消したい用途）
  const clearActiveItem = (activeKey) => {
    try {
      if (activeKey) setDismissedActiveKey(activeKey);
    } catch (e) {}
  };
        // ✅ 予約ActiveItem を手動で消す（UIを閉じたら消す用途）
      const clearActiveReservationItem = (activeKey) => {
        try {
          setActiveReservationItem((prev) => {
            if (!prev) return null;
            if (activeKey && prev.activeKey !== activeKey) return prev; // 安全
            return null;
          });
        } catch (e) {}
      };

  return {
    memberData,
    rawPaymentHistory,
    subscriptions,        // ←追加
    activeSubscriptions,
    productMap,
    priceMap,      // ←一応返してもいい（デバッグ用）
    planCard,      // ✅ 追加
    inventory,

    activeItems: mergedActiveItems,
    clearActiveItem,

    // ✅ 追加：予約の残り時間用
    activeReservationItem,
    clearActiveReservationItem,

    systemMessage: validSystemMessage,
    reservations,
    loading,

    customerUid,
    entitlements,
    pendingGifts,
    pendingReservationCount,
    planActiveEnt,
  };
};


//useDashboardData.js
//テナント済み